import { ComponentDescriptor, UiDefaultValueDefinition } from 'app/infrastructure/ui/types-uischema';
import { deepEquals } from 'app/infrastructure/js';
import { ObjectWithChildren } from 'app/infrastructure/ui/builder/types';

/**
 * When adding a child, all default values must also be calculated and added.
 * The information about whether to add a default value is part of the component descriptors.
 */
export function resolveComponentDefaultValues
  (rootDescriptor: ComponentDescriptor): UiDefaultValueDefinition[] {

  const entityTypePathEquals = (descriptor: ComponentDescriptor) => {
    return deepEquals(descriptor.entityTypePath, rootDescriptor.entityTypePath);
  };

  const addDefaultValue = (descriptor: ComponentDescriptor, defaultValues: UiDefaultValueDefinition[]) => {
    if (descriptor.defaultValue && entityTypePathEquals(descriptor)) {
      defaultValues.push({ dataTag: descriptor.dataTag, defaultValue: descriptor.defaultValue });
    }
  };

  // Get definitions recursively based on entityPath. Stop at children empty or domain iterate === true.
  const iterateChildren = (defaultValues: UiDefaultValueDefinition[], descriptor: ComponentDescriptor) => {
    if (!descriptor.iterate) {
      addDefaultValue(descriptor, defaultValues);

      descriptor.children && descriptor.children.map(child => iterateChildren(defaultValues, child));
    }

    return defaultValues;
  };

  return rootDescriptor.children && rootDescriptor.children.reduce(iterateChildren, []);
}

/**
 * Finds objects containing the key name and/or dataTag.
 * Returns an array of objects that are matching the criterias.
 * @param {ObjectWithChildren} object - a tree of objects, children need to be in the "children" key.
 * @param {string} originalKey - searched key in the object tree
 * @return {ObjectWithChildren[]} dataTag? - searched dataTag value, optional
 */
export function deepFindByKey(object: ObjectWithChildren, originalKey: string, dataTag?: string, matches = []): ObjectWithChildren[] {
  if (object != null) {
    if (Array.isArray(object)) {
      for (const arrayItem of object) {
        deepFindByKey(arrayItem, originalKey, dataTag, matches);
      }
    } else if (typeof object == 'object') {

      for (const key of Object.keys(object)) {
        if (
          key === originalKey &&
          (!dataTag || (!!dataTag && object.hasOwnProperty('dataTag') && object.dataTag === dataTag))
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { children, ...rest } = object;
          !!object[originalKey] && matches.push(rest);
        } else {
          key === 'children' && deepFindByKey(object[key], originalKey, dataTag, matches);
        }
      }
    }
  }

  return matches;
}
